import React, { useEffect, useMemo, useState } from "react";
import { fetchJson } from "../../lib/api";
import { formatAmount, channelLabel } from "../../lib/format-utils";
import type { ChannelId, StatisticsResponse } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../card";

type ChannelBreakdownItem = {
  label: string;
  value: string;
};

const StatCard = ({
  label,
  value,
  breakdownItems,
  breakdownMessage,
}: {
  label: string;
  value: string;
  breakdownItems: ChannelBreakdownItem[];
  breakdownMessage: string | null;
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-sm font-medium text-muted">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-2xl font-semibold text-ink">{value}</p>
      {breakdownItems.length ? (
        <div className="mt-2 flex flex-wrap gap-4">
          {breakdownItems.map((item) => (
            <div key={item.label} className="space-y-1">
              <p className="text-sm font-semibold text-ink">{item.value}</p>
              <p className="text-xs text-muted">{item.label}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-xs text-muted">{breakdownMessage ?? "—"}</p>
      )}
    </CardContent>
  </Card>
);

export const StatisticsView = ({
  houseId,
  search,
  setSearch,
}: {
  houseId: number | null;
  search: string;
  setSearch: (nextSearch: string, options?: { replace?: boolean }) => void;
}) => {
  type BookingChannelId = Exclude<ChannelId, "eloverblik">;
  const { t, locale } = useI18n();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [report, setReport] = useState<StatisticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!houseId) {
      setReport(null);
      return;
    }
    const yearParam = Number.parseInt(searchParams.get("year") ?? "", 10);
    const yearQuery = Number.isNaN(yearParam) ? "" : `?year=${yearParam}`;
    setIsLoading(true);
    setError(null);
    fetchJson<StatisticsResponse>(`/api/houses/${houseId}/statistics${yearQuery}`)
      .then((response) => {
        setReport(response);
        if (Number.isNaN(yearParam) || response.year !== yearParam) {
          const nextParams = new URLSearchParams(search);
          nextParams.set("year", String(response.year));
          setSearch(`?${nextParams.toString()}`, { replace: true });
        }
      })
      .catch((loadError) => {
        setError(
          loadError instanceof Error
            ? loadError.message
            : t("stats.loadFailed", "Failed to load statistics"),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [houseId, search, searchParams, setSearch]);

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("stats.title", "Statistics")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("stats.selectHouse", "Select a house to review statistics.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const availableYears = report?.years.length ? report.years : report ? [report.year] : [];
  const activeYear = report?.year ?? new Date().getUTCFullYear();
  const orderedChannelIds: BookingChannelId[] = ["villavilla", "admiralstrand"];
  const channelIds: BookingChannelId[] =
    report?.byChannel
      .map((channel) => channel.channelId)
      .filter((channelId) => channelId !== "eloverblik") ?? [];
  const channelIdSet = new Set(channelIds);
  const sortedChannels = [
    ...orderedChannelIds.filter((channelId) => channelIdSet.has(channelId)),
    ...channelIds.filter((channelId) => !orderedChannelIds.includes(channelId)),
  ];
  const numberLocale = locale === "da" ? "da-DK" : "en-US";
  const formatCount = (value: number) =>
    new Intl.NumberFormat(numberLocale, { maximumFractionDigits: 0 }).format(value);
  const formatAverageDays = (value: number) => {
    if (!Number.isFinite(value) || value <= 0) {
      return "—";
    }
    return t("stats.daysValue", "{days} days", { days: value.toFixed(1) });
  };
  const formatDailyAmount = (value: number) => {
    if (!Number.isFinite(value) || value <= 0) {
      return "—";
    }
    return formatAmount(value, "DKK");
  };

  const buildMetricBreakdown = (
    formatter: (value: number) => string,
    picker: (value: StatisticsResponse["byChannel"][number]) => number,
  ) => {
    if (!report || sortedChannels.length === 0) {
      return [] as ChannelBreakdownItem[];
    }
    return sortedChannels.map((channelId) => {
      const channelStats =
        report.byChannel.find((channel) => channel.channelId === channelId) ?? null;
      const value = channelStats ? formatter(picker(channelStats)) : "—";
      return {
        label: channelLabel(channelId),
        value,
      };
    });
  };

  const getUtilitiesPerDay = (stats: StatisticsResponse["byChannel"][number]) => {
    if (!stats.bookedDays) {
      return 0;
    }
    if (!Number.isFinite(stats.bookedDays) || stats.bookedDays <= 0) {
      return 0;
    }
    return stats.totalUtilitiesIncome / stats.bookedDays;
  };

  const totalUtilitiesPerDay =
    report && report.totals.bookedDays
      ? report.totals.totalUtilitiesIncome / report.totals.bookedDays
      : 0;

  const breakdownMessage = !report
    ? "—"
    : sortedChannels.length === 0
      ? t("stats.noChannelData", "No channel data.")
      : null;

  const updateYear = (value: string) => {
    const nextParams = new URLSearchParams(search);
    nextParams.set("year", value);
    setSearch(`?${nextParams.toString()}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-lg font-semibold text-ink">{t("stats.title", "Statistics")}</p>
          <p className="text-sm text-muted">
            {t(
              "stats.subtitle",
              "Overview of booking performance and income for the selected year.",
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-ink" htmlFor="stats-year">
              {t("common.year", "Year")}
            </label>
            <select
              id="stats-year"
              className="h-9 rounded-md border px-3 text-sm brand-input brand-focus"
              value={String(activeYear)}
              onChange={(event) => updateYear(event.target.value)}
              disabled={!availableYears.length}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {isLoading ? (
        <p className="text-sm text-muted">{t("stats.loading", "Loading statistics...")}</p>
      ) : null}
      {error ? <p className="text-sm text-error">{error}</p> : null}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard
          label={t("stats.bookings", "Bookings")}
          value={report ? formatCount(report.totals.bookingCount) : "—"}
          breakdownItems={buildMetricBreakdown(formatCount, (stats) => stats.bookingCount)}
          breakdownMessage={breakdownMessage}
        />
        <StatCard
          label={t("stats.bookedDays", "Booked days")}
          value={report ? formatCount(report.totals.bookedDays) : "—"}
          breakdownItems={buildMetricBreakdown(formatCount, (stats) => stats.bookedDays)}
          breakdownMessage={breakdownMessage}
        />
        <StatCard
          label={t("stats.avgStay", "Average length of stay")}
          value={report ? formatAverageDays(report.totals.averageStayLength) : "—"}
          breakdownItems={buildMetricBreakdown(formatAverageDays, (stats) => stats.averageStayLength)}
          breakdownMessage={breakdownMessage}
        />
        <StatCard
          label={t("stats.avgPricePerDay", "Average price per day")}
          value={report ? formatAmount(report.totals.averagePricePerDay, "DKK") : "—"}
          breakdownItems={buildMetricBreakdown(
            (value) => formatAmount(value, "DKK"),
            (stats) => stats.averagePricePerDay,
          )}
          breakdownMessage={breakdownMessage}
        />
        <StatCard
          label={t("stats.totalRentalIncome", "Total rental income")}
          value={report ? formatAmount(report.totals.totalRentalIncome, "DKK") : "—"}
          breakdownItems={buildMetricBreakdown(
            (value) => formatAmount(value, "DKK"),
            (stats) => stats.totalRentalIncome,
          )}
          breakdownMessage={breakdownMessage}
        />
        <StatCard
          label={t("stats.utilitiesIncome", "Utilities income")}
          value={report ? formatAmount(report.totals.totalUtilitiesIncome, "DKK") : "—"}
          breakdownItems={buildMetricBreakdown(
            (value) => formatAmount(value, "DKK"),
            (stats) => stats.totalUtilitiesIncome,
          )}
          breakdownMessage={breakdownMessage}
        />
        <StatCard
          label={t("stats.utilitiesPerDay", "Utilities income per day")}
          value={report ? formatDailyAmount(totalUtilitiesPerDay) : "—"}
          breakdownItems={buildMetricBreakdown(formatDailyAmount, getUtilitiesPerDay)}
          breakdownMessage={breakdownMessage}
        />
        <StatCard
          label={t("stats.totalIncome", "Total income")}
          value={report ? formatAmount(report.totals.totalIncome, "DKK") : "—"}
          breakdownItems={buildMetricBreakdown(
            (value) => formatAmount(value, "DKK"),
            (stats) => stats.totalIncome,
          )}
          breakdownMessage={breakdownMessage}
        />
      </div>
    </div>
  );
};
